import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { toast } from 'sonner'
import { AlertTriangle, ChevronsDownUp, ChevronsUpDown, Link as LinkIcon, RefreshCw } from 'lucide-react'
import {
  BulkActionBar,
  Button,
  Checkbox,
  Chip,
  DataTable,
  EmptyState,
  groupRows,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  useTableGrouping,
  type TableGroup,
} from '@/components/ui'
import { useBulkSetFlagStatus, useVendorPriceChangeFlags } from '../api/queries'
import type { FlagStatus, VendorPriceChangeFlag } from '../api/types'
import { ManualEntryDrawer } from '../components/ManualEntryDrawer'
import { PriceChangesFilterRail } from '../components/PriceChangesFilterRail'
import {
  applyQueueFilters,
  DEFAULT_QUEUE_FILTERS,
  directionCounts as countByDirection,
  formatPct,
  productOf,
  tierOf,
  vendorCounts,
  vendorOf,
  type QueueFilters,
} from '../utils/queue-filters'

/**
 * Sprint 37 — the review queue: every open/reviewing vendor price-change
 * flag. The whole active queue loads once (both directions, no row cap)
 * and is filtered, searched, sorted and grouped client-side, so counts
 * and group totals always cover every row. Default sort is % change,
 * biggest first (Chris's "flag any increase, sort by impact").
 */

type GroupBy = 'none' | 'vendor' | 'product' | 'vendor_product'

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'product', label: 'Product' },
  { value: 'vendor_product', label: 'Vendor · Product' },
]

const STATUS_LABEL: Record<FlagStatus, string> = {
  open: 'Open',
  reviewing: 'Reviewing',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

const usd = (v: number) => v.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

function Stop({ children }: { children: React.ReactNode }) {
  return <span onClick={(e) => e.stopPropagation()}>{children}</span>
}

function groupKeyOf(groupBy: GroupBy, f: VendorPriceChangeFlag): string {
  if (groupBy === 'vendor') return vendorOf(f)
  if (groupBy === 'product') return productOf(f)
  return `${vendorOf(f)} · ${productOf(f)}`
}

export function VendorPriceChangesQueuePage() {
  const navigate = useNavigate()
  const [manualEntryOpen, setManualEntryOpen] = useState(false)
  const [filters, setFilters] = useState<QueueFilters>(DEFAULT_QUEUE_FILTERS)
  const [railCollapsed, setRailCollapsed] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [bulkNote, setBulkNote] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const { groupBy: rawGroupBy, setGroupBy, collapsedGroups, setCollapsedGroups, toggleGroup } =
    useTableGrouping('vendor-price-changes')
  const groupBy = (GROUP_OPTIONS.some((o) => o.value === rawGroupBy) ? rawGroupBy : 'none') as GroupBy
  const bulk = useBulkSetFlagStatus()

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || !(e.target instanceof HTMLElement)) return
      const typing =
        e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable
      if (typing) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The active queue — resolved/dismissed flags belong on History. Both
  // directions in one fetch; the Direction filter narrows client-side.
  const { data, isLoading, isFetching, isError, error, refetch } = useVendorPriceChangeFlags({
    status: 'open,reviewing',
    sort: 'pct_change',
  })
  const allFlags = useMemo<VendorPriceChangeFlag[]>(() => data?.flags ?? [], [data])
  const total = data?.total ?? allFlags.length
  const truncated = Boolean(data?.truncated)

  const filtered = useMemo(
    () => applyQueueFilters(allFlags, filters, debouncedSearch),
    [allFlags, filters, debouncedSearch],
  )
  const vendorOptions = useMemo(
    () => vendorCounts(allFlags, filters, debouncedSearch),
    [allFlags, filters, debouncedSearch],
  )

  const dirCounts = useMemo(
    () => countByDirection(allFlags, filters, debouncedSearch),
    [allFlags, filters, debouncedSearch],
  )
  // Matches the Direction filter is hiding right now — surfaced next to
  // the count so a vendor search never silently shows only half its rows.
  const hiddenByDirection =
    filters.direction === 'increase' ? dirCounts.decrease
      : filters.direction === 'decrease' ? dirCounts.increase
        : 0

  const metrics = useMemo(() => {
    const increases = allFlags.filter((f) => f.direction === 'increase')
    return {
      increases: increases.length,
      vendors: new Set(increases.map(vendorOf)).size,
      largest: increases.reduce((m, f) => Math.max(m, f.pct_change), 0),
      collisions: allFlags.reduce((n, f) => n + (f.open_po_collision_count || 0), 0),
    }
  }, [allFlags])

  const groups = useMemo<TableGroup<VendorPriceChangeFlag>[] | undefined>(() => {
    if (groupBy === 'none') return undefined
    const maxPct = new Map<string, number>()
    for (const f of filtered) {
      const k = groupKeyOf(groupBy, f)
      const prev = maxPct.get(k)
      if (prev === undefined || Math.abs(f.pct_change) > Math.abs(prev)) maxPct.set(k, f.pct_change)
    }
    const built = groupRows(filtered, {
      keyOf: (f) => groupKeyOf(groupBy, f),
      labelOf: (k) => {
        const m = maxPct.get(k)
        return m === undefined ? k : `${k} · largest ${formatPct(m)}`
      },
      accentOf: (k) => ((maxPct.get(k) ?? 0) >= 25 ? 'danger' : undefined),
    })
    // Biggest groups first: that's where the triage work is.
    return built.sort((a, b) => b.items.length - a.items.length || a.key.localeCompare(b.key))
  }, [groupBy, filtered])

  // Only rows still shown count: a selection the user filtered out of view
  // must not ride along into a bulk action.
  const selectedIds = useMemo(
    () => filtered.filter((f) => selection[String(f.id)]).map((f) => f.id),
    [filtered, selection],
  )

  function runBulk(status: FlagStatus) {
    const ids = selectedIds
    bulk.mutate(
      { flagIds: ids, status, notes: bulkNote.trim() || undefined },
      {
        onSuccess: (res) => {
          const verb = status === 'dismissed' ? 'Dismissed' : status === 'reviewing' ? 'Marked reviewing' : 'Updated'
          if (res.failed.length) {
            toast.warning(`${verb} ${res.updated.length} of ${ids.length}. ${res.failed.length} could not be changed.`)
          } else {
            toast.success(`${verb} ${res.updated.length} price change${res.updated.length === 1 ? '' : 's'}.`)
          }
          setSelection({})
          setBulkNote('')
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Bulk update failed.')
        },
      },
    )
  }

  const columns = useMemo<ColumnDef<VendorPriceChangeFlag, unknown>[]>(
    () => [
      {
        id: '__select',
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all shown"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Stop>
            <Checkbox
              aria-label={`Select ${productOf(row.original)}`}
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </Stop>
        ),
        enableSorting: false,
      },
      { id: 'vendor_name', header: 'Vendor', accessorFn: vendorOf },
      { id: 'product_name', header: 'Product', accessorFn: productOf },
      {
        id: 'tier',
        header: 'Tier',
        accessorFn: tierOf,
        cell: ({ row }) => (
          <span className="text-role-metadata" title={row.original.tier_key}>
            {tierOf(row.original)}
          </span>
        ),
      },
      {
        id: 'new_price',
        header: 'Vendor price',
        accessorKey: 'new_price',
        meta: { numeric: true },
        cell: ({ row: { original: f } }) => (
          <span className="tabular-nums">
            <span className="text-tertiary">{usd(f.old_price)} → </span>
            {usd(f.new_price)}
          </span>
        ),
      },
      {
        id: 'abs_change',
        header: '$ change',
        accessorKey: 'abs_change',
        meta: { numeric: true },
        cell: (ctx) => {
          const v = ctx.getValue() as number
          return <span className="tabular-nums">{v > 0 ? '+' : ''}{usd(v)}</span>
        },
      },
      {
        id: 'pct_change',
        header: '% change',
        accessorKey: 'pct_change',
        meta: { numeric: true },
        cell: (ctx) => {
          const v = ctx.getValue() as number
          const tone = v > 0 ? 'danger' : v < 0 ? 'success' : 'neutral'
          return <Chip size="xs" variant={tone}>{formatPct(v)}</Chip>
        },
      },
      {
        id: 'open_po_collision_count',
        header: 'Open POs',
        accessorKey: 'open_po_collision_count',
        meta: { numeric: true },
        cell: (ctx) => {
          const v = ctx.getValue() as number
          if (!v) return <span className="text-tertiary">—</span>
          return (
            <Chip size="xs" variant="danger" title="Open POs still priced against a stale vendor cost — money already committed">
              <AlertTriangle size={10} /> {v}
            </Chip>
          )
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (ctx) => {
          const s = ctx.getValue() as FlagStatus
          return <Chip size="xs" variant={s === 'reviewing' ? 'info' : 'neutral'}>{STATUS_LABEL[s] ?? s}</Chip>
        },
      },
      {
        id: 'detected_at',
        header: 'Detected',
        accessorFn: (f) => new Date(f.detected_at).getTime(),
        cell: ({ row }) => new Date(row.original.detected_at).toLocaleDateString(),
      },
    ],
    [],
  )

  const filtersActive = filtered.length !== allFlags.length

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Vendor Price Changes"
        description="Detected vendor cost changes, tier-aware, with per-customer margin-preserving price suggestions. Review, adjust, and approve before anything writes to a customer's pricelist."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Open increases" value={metrics.increases.toLocaleString()} />
          <MetricCard variant="inline" label="Vendors" value={metrics.vendors.toLocaleString()} />
          <MetricCard
            variant="inline"
            label="Largest increase"
            value={metrics.increases ? formatPct(metrics.largest) : '—'}
            valueTone={metrics.largest >= 25 ? 'danger' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Open PO collisions"
            value={metrics.collisions.toLocaleString()}
            valueTone={metrics.collisions > 0 ? 'danger' : 'default'}
          />
        </MetricStrip>
        <Button variant="primary" size="sm" onClick={() => setManualEntryOpen(true)}>
          Log a price change
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate({ to: '/vendor-price-changes/upcoming' })}
        >
          Upcoming
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate({ to: '/vendor-price-changes/history' })}
        >
          <LinkIcon size={12} /> History
        </Button>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <PriceChangesFilterRail
          filters={filters}
          onChange={setFilters}
          vendorOptions={vendorOptions}
          directionCounts={dirCounts}
          collapsed={railCollapsed}
          onToggleCollapsed={() => setRailCollapsed((v) => !v)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <Toolbar>
            <SearchInput
              ref={searchRef}
              containerClassName="flex-1 max-w-none"
              placeholder="Search vendor, product or tier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
          </Toolbar>
          <Toolbar>
            <ToolbarLeft>
              <label className="flex items-center gap-1.5 whitespace-nowrap text-role-toolbar">
                Group by
                <Select
                  aria-label="Group by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </label>
              {groups && groups.length > 0 ? (
                <>
                  <ToolbarDivider />
                  <Button variant="ghost" size="sm" onClick={() => setCollapsedGroups(groups.map((g) => g.key))}>
                    <ChevronsDownUp size={14} /> Collapse all
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setCollapsedGroups([])}>
                    <ChevronsUpDown size={14} /> Expand all
                  </Button>
                </>
              ) : null}
              {truncated ? (
                <Chip size="xs" variant="warning">
                  Showing {allFlags.length.toLocaleString()} of {total.toLocaleString()}
                </Chip>
              ) : null}
            </ToolbarLeft>
            <ToolbarRight>
              {hiddenByDirection > 0 ? (
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, direction: 'all' }))}
                  className="cursor-pointer whitespace-nowrap border-none bg-transparent p-0 text-xs text-accent-text hover:underline"
                >
                  {hiddenByDirection.toLocaleString()}{' '}
                  {filters.direction === 'increase' ? 'decreases' : 'increases'} hidden · Show all
                </button>
              ) : null}
              <ToolbarCount>
                {filtersActive
                  ? `${filtered.length.toLocaleString()} of ${allFlags.length.toLocaleString()}`
                  : `${allFlags.length.toLocaleString()} active`}
                {groups ? ` · ${groups.length.toLocaleString()} groups` : ''}
              </ToolbarCount>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Refresh"
                loading={isFetching && !isLoading}
                onClick={() => refetch()}
              >
                <RefreshCw size={14} />
              </Button>
            </ToolbarRight>
          </Toolbar>

          <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-4">
            {isError ? (
              <QueryErrorState error={error} onRetry={() => void refetch()} subject="vendor price changes" />
            ) : (
              <DataTable
                data={filtered}
                columns={columns}
                loading={isLoading}
                getRowId={(r) => String(r.id)}
                selection={selection}
                onSelectionChange={setSelection}
                onRowClick={(r) =>
                  navigate({ to: '/vendor-price-changes/$flagId', params: { flagId: String(r.id) } })
                }
                criticalRowPredicate={(r) => r.open_po_collision_count > 0}
                groups={groups}
                collapsedGroups={collapsedGroups}
                onToggleGroup={toggleGroup}
                initialSorting={[{ id: 'pct_change', desc: true }]}
                uppercaseHeaders={false}
                moduleId="vendor-price-changes"
                maxHeight="100%"
                minTableWidth={1150}
                columnWidths={{
                  __select: 40,
                  vendor_name: 230,
                  product_name: undefined,
                  tier: 140,
                  new_price: 190,
                  abs_change: 110,
                  pct_change: 100,
                  open_po_collision_count: 90,
                  status: 100,
                  detected_at: 105,
                }}
                emptyState={
                  allFlags.length === 0 ? (
                    <EmptyState
                      variant="no-data"
                      title="No open vendor price changes"
                      description="Detected changes from confirmed POs, manual entries, or vendor pricelists will show up here."
                    />
                  ) : (
                    <EmptyState
                      variant="no-match"
                      title="Nothing matches these filters"
                      description="Clear a filter or the search to see more."
                      action={
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setFilters(DEFAULT_QUEUE_FILTERS)
                            setSearch('')
                          }}
                        >
                          Clear filters
                        </Button>
                      }
                    />
                  )
                }
              />
            )}
          </div>
        </div>
      </div>

      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        <input
          type="text"
          value={bulkNote}
          onChange={(e) => setBulkNote(e.target.value)}
          placeholder="Note (optional)"
          aria-label="Note for the selected price changes"
          className="h-8 w-56 rounded-xs border border-border-default bg-surface px-2 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
        <Button variant="secondary" size="sm" loading={bulk.isPending} onClick={() => runBulk('reviewing')}>
          Mark reviewing
        </Button>
        <Button variant="secondary" size="sm" loading={bulk.isPending} onClick={() => runBulk('dismissed')}>
          Dismiss
        </Button>
      </BulkActionBar>

      <ManualEntryDrawer open={manualEntryOpen} onOpenChange={setManualEntryOpen} />
    </div>
  )
}
